import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type Batch,
  type BatchStock,
  type CreateBatchParams,
  type DistributeParams,
  type Holding,
  type MovementsSummary,
  type Portfolio,
  type ReceiveBatchParams,
  type Reception,
  type RecordCustodyParams,
  type RecordReceptionParams,
} from "../../domain/entities/batch";
import {
  AdvanceBatchError,
  CreateBatchError,
  DistributeError,
  ListBatchesError,
  ListCustodyError,
  ListHoldingsError,
  ListInventoryError,
  ListReceptionsError,
  ReceiveBatchError,
  RecordCustodyError,
  RecordReceptionError,
  ReconcileBatchError,
  UploadCustodyDocError,
} from "../../domain/errors/errors";
import { type BatchesRepository } from "../../domain/repos/batches-repo";
import {
  type BatchListResponse,
  type BatchResponse,
  type CustodyBoardResponse,
  type HoldingsResponse,
  type InventoryResponse,
  type MovementsSummaryResponse,
  type ReceptionsResponse,
} from "../../infrastructure/responses/batches-responses";

export class BatchesRepo implements BatchesRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async listBatches(): Promise<Batch[]> {
    try {
      const response = await this.apiClient.get<BatchListResponse>("v1/batches");
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListBatchesError(message, apiError);
    }
  }

  async getInventory(): Promise<BatchStock[]> {
    try {
      const response = await this.apiClient.get<InventoryResponse>("v1/inventory");
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListInventoryError(message, apiError);
    }
  }

  async getMovementsSummary(businessDate: string): Promise<MovementsSummary> {
    try {
      const response = await this.apiClient.get<MovementsSummaryResponse>(
        `v1/inventory/movements-summary?business_date=${encodeURIComponent(businessDate)}`,
      );
      return response.data ?? { distribution: 0, delivery: 0, returned: 0, receipt: 0 };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListInventoryError(message, apiError);
    }
  }

  async getHoldings(businessDate: string): Promise<Holding[]> {
    try {
      const response = await this.apiClient.get<HoldingsResponse>(
        `v1/inventory/holdings?business_date=${encodeURIComponent(businessDate)}`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListHoldingsError(message, apiError);
    }
  }

  async distribute(params: DistributeParams): Promise<void> {
    try {
      // The request interceptor snake_cases the body (recipientUserId ->
      // recipient_user_id). `type` is fixed: warehouse → recipient
      // (supervisor/operations).
      await this.apiClient.post("v1/inventory/movements", {
        batchId: params.batchId,
        type: "distribution",
        quantity: params.quantity,
        recipientUserId: params.recipientUserId,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new DistributeError(message, apiError);
    }
  }

  async getReceptions(businessDate: string): Promise<Reception[]> {
    try {
      const response = await this.apiClient.get<ReceptionsResponse>(
        `v1/day-receptions?business_date=${encodeURIComponent(businessDate)}`,
      );
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListReceptionsError(message, apiError);
    }
  }

  async recordReception(params: RecordReceptionParams): Promise<void> {
    try {
      // The request interceptor snake_cases the body (ambassadorId ->
      // ambassador_id, returnedCount -> returned_count, ...).
      await this.apiClient.post("v1/day-receptions", {
        ambassadorId: params.ambassadorId,
        businessDate: params.businessDate,
        returnedCount: params.returnedCount,
        portfoliosCount: params.portfoliosCount,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RecordReceptionError(message, apiError);
    }
  }

  async getCustodyBoard(status: string, q: string): Promise<Portfolio[]> {
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (q) params.set("q", q);
      const suffix = params.toString() ? `?${params.toString()}` : "";
      const response = await this.apiClient.get<CustodyBoardResponse>(`v1/custody${suffix}`);
      return response.data?.items ?? [];
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ListCustodyError(message, apiError);
    }
  }

  async recordCustody(params: RecordCustodyParams): Promise<void> {
    // Upload the two signed scans first (they gate archiving server-side), then
    // record the physical location.
    await this.uploadCustodyDoc(params.requestId, "contrato", params.contrato);
    await this.uploadCustodyDoc(params.requestId, "hoja_recepcion", params.receptionSheet);
    try {
      await this.apiClient.post(`v1/requests/${params.requestId}/custody`, {
        box: params.box,
        folder: params.folder,
        shelf: params.shelf,
      });
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new RecordCustodyError(message, apiError);
    }
  }

  private async uploadCustodyDoc(requestId: string, type: string, file: Blob): Promise<void> {
    try {
      const form = new FormData();
      form.append("type", type);
      form.append("file", file, `${type}`);
      await this.apiClient.post(`v1/requests/${requestId}/captures`, form);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new UploadCustodyDocError(message, apiError);
    }
  }

  async createBatch(params: CreateBatchParams): Promise<Batch> {
    try {
      // The request interceptor snake_cases the body (requestedQuantity ->
      // requested_quantity).
      const response = await this.apiClient.post<BatchResponse>("v1/batches", {
        code: params.code,
        requestedQuantity: params.requestedQuantity,
      });
      return response.data as Batch;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new CreateBatchError(message, apiError);
    }
  }

  async advanceBatch(batchId: string): Promise<Batch> {
    try {
      const response = await this.apiClient.post<BatchResponse>(
        `v1/batches/${batchId}/advance`,
        {},
      );
      return response.data as Batch;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new AdvanceBatchError(message, apiError);
    }
  }

  async receiveBatch(params: ReceiveBatchParams): Promise<Batch> {
    try {
      const response = await this.apiClient.post<BatchResponse>(
        `v1/batches/${params.batchId}/reception`,
        { receivedQuantity: params.receivedQuantity },
      );
      return response.data as Batch;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ReceiveBatchError(message, apiError);
    }
  }

  async reconcileBatch(batchId: string): Promise<Batch> {
    try {
      const response = await this.apiClient.post<BatchResponse>(
        `v1/batches/${batchId}/reconciliation`,
        {},
      );
      return response.data as Batch;
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ReconcileBatchError(message, apiError);
    }
  }
}
